<template>
  <div class="p-0">
    <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-3">
      <h1 class="text-lg md:text-xl font-semibold m-0">Гарантии на котлы</h1>
      <div class="flex gap-x-2">
        <el-input
          v-model="query"
          size="small"
          placeholder="Серийник, телефон или имя"
          clearable
          class="flex-1 md:flex-none md:w-64"
          @keyup.enter.native="load"
          @clear="load"
        ></el-input>
        <el-button size="small" @click="load">Поиск</el-button>
      </div>
    </div>

    <el-tabs v-model="status" @tab-click="load">
      <el-tab-pane :label="`Новые (${counts.new || 0})`" name="new"></el-tab-pane>
      <el-tab-pane :label="`Активированные (${counts.activated || 0})`" name="activated"></el-tab-pane>
      <el-tab-pane :label="`Отклонённые (${counts.rejected || 0})`" name="rejected"></el-tab-pane>
      <el-tab-pane label="Все" name="all"></el-tab-pane>
    </el-tabs>

    <!-- Десктоп: таблица -->
    <el-table
      v-loading="loading"
      :data="warranties"
      size="small"
      empty-text="Заявок нет"
      class="hidden md:block"
      @row-click="openItem"
    >
      <el-table-column label="Заявка" width="140">
        <template slot-scope="{ row }">{{ formatDate(row.created_at) }}</template>
      </el-table-column>
      <el-table-column prop="model" label="Модель" width="90"></el-table-column>
      <el-table-column prop="serial_number" label="Серийный номер" width="180"></el-table-column>
      <el-table-column prop="client_name" label="Клиент"></el-table-column>
      <el-table-column prop="phone" label="Телефон" width="150"></el-table-column>
      <el-table-column label="Куплен" width="120">
        <template slot-scope="{ row }">{{ formatDay(row.purchase_date) }}</template>
      </el-table-column>
      <el-table-column label="Статус" width="150">
        <template slot-scope="{ row }">
          <el-tag size="mini" :type="statusType(row.status)" effect="plain">
            {{ statusLabel(row.status) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="Активирована" width="140">
        <template slot-scope="{ row }">
          {{ row.activated_at ? formatDate(row.activated_at, "dd.MM.yyyy") : "—" }}
        </template>
      </el-table-column>
      <el-table-column label="Действует до" width="130">
        <template slot-scope="{ row }">
          {{ row.warranty_until ? formatDay(row.warranty_until) : "—" }}
        </template>
      </el-table-column>
    </el-table>

    <!-- Мобильный: карточки -->
    <div v-loading="loading" class="md:hidden flex flex-col gap-y-2">
      <div
        v-for="row in warranties"
        :key="row.id"
        class="border border-solid border-gray-200 rounded-lg p-3 bg-white"
        @click="openItem(row)"
      >
        <div class="flex items-start justify-between gap-x-2">
          <div class="font-medium break-all">{{ row.serial_number }}</div>
          <el-tag size="mini" :type="statusType(row.status)" effect="plain">
            {{ statusLabel(row.status) }}
          </el-tag>
        </div>
        <div class="text-sm text-gray-700 mt-1">{{ row.model }} · {{ row.client_name }}</div>
        <a class="text-sm text-blue-600" :href="`tel:${row.phone}`" @click.stop>{{ row.phone }}</a>
        <div class="text-xs text-gray-500 mt-1">
          Заявка {{ formatDate(row.created_at, "dd.MM.yyyy") }} ·
          куплен {{ formatDay(row.purchase_date) }}
          <template v-if="row.activated_at">
            · активирована {{ formatDate(row.activated_at, "dd.MM.yyyy") }}
          </template>
          <template v-if="row.warranty_until">
            · до {{ formatDay(row.warranty_until) }}
          </template>
        </div>
      </div>
      <div v-if="!loading && warranties.length === 0" class="text-gray-500 text-center py-6">
        Заявок нет
      </div>
    </div>
  </div>
</template>

<script>
import { format } from "date-fns";
import { getWarranties } from "@/api/warranty";

export default {
  name: "WarrantyList",
  data: () => ({
    loading: false,
    warranties: [],
    counts: {},
    status: "new",
    query: "",
  }),
  async created() {
    await this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const params = {};
        if (this.status !== "all") params.status = this.status;
        if (this.query.trim()) params.q = this.query.trim();
        const data = await getWarranties(params);
        this.warranties = data.warranties;
        this.counts = data.counts || {};
      } catch (e) {
        this.$message.error(this.errorText(e));
      }
      this.loading = false;
    },
    openItem(row) {
      this.$router.push({ name: "warranty-item", params: { id: row.id } });
    },
    // Календарные даты приходят строками ГГГГ-ММ-ДД: у даты покупки нет времени,
    // и пересчитывать её в таймзону браузера нельзя — уедет на соседний день.
    formatDay(value) {
      if (!value) return "";
      const [y, m, d] = value.split("-");
      return `${d}.${m}.${y}`;
    },
    // Моменты времени (created_at, activated_at) приходят unix-таймстемпами (core/encoder.py)
    formatDate(value, mask = "dd.MM.yyyy HH:mm") {
      if (!value) return "";
      const ms = value > 1e10 ? value : value * 1000;
      return format(new Date(ms), mask);
    },
    statusLabel(status) {
      return { new: "Новая", activated: "Активирована", rejected: "Отклонена" }[status] || status;
    },
    statusType(status) {
      return { new: "info", activated: "success", rejected: "danger" }[status] || "";
    },
    errorText(error) {
      return (error.response && error.response.data && error.response.data.message) || error.message;
    },
  },
};
</script>
